import { useRef, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { CaretDown } from "@phosphor-icons/react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { Reveal, SectionHeader } from "./primitives";

const Roadmap = () => {
  const config = useSiteConfig();
  const [open, setOpen] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 65%"] });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });

  return (
    <section id="roadmap" className="relative py-24 lg:py-36 border-t border-line" data-testid="roadmap-section">
      <div className="section-shell">
        <SectionHeader
          index={12}
          eyebrow="Roadmap"
          title={<>Five Phases.<br />One Direction.</>}
          sub={config.roadmapDisclaimer}
          testId="roadmap-header"
        />
        <div ref={ref} className="mt-14 relative">
          <div className="absolute left-[7px] sm:left-[11px] top-2 bottom-2 w-px bg-line" aria-hidden="true" />
          <motion.div
            className="absolute left-[7px] sm:left-[11px] top-2 bottom-2 w-px bg-teal origin-top"
            style={{ scaleY: progress }}
            aria-hidden="true"
          />
          <div className="space-y-4">
            {config.roadmap.map((phase, i) => {
              const isOpen = open === i;
              return (
                <Reveal key={phase.phase} delay={i * 0.05} y={18} testId={`roadmap-phase-${i + 1}`}>
                  <div className="relative pl-10 sm:pl-14">
                    <span
                      className={`absolute left-0 top-6 h-[15px] w-[15px] sm:h-[23px] sm:w-[23px] rounded-full border-2 bg-navy ${isOpen ? "border-teal" : "border-line"}`}
                      aria-hidden="true"
                    >
                      <span className={`absolute inset-[3px] sm:inset-[5px] rounded-full ${isOpen ? "bg-teal" : "bg-line"}`} />
                    </span>
                    <div className={`card-surface rounded-xl overflow-hidden ${isOpen ? "border-teal/50" : ""}`}>
                      <button
                        className="w-full flex items-center gap-4 sm:gap-6 px-6 sm:px-8 py-5 sm:py-6 text-left"
                        onClick={() => setOpen(isOpen ? -1 : i)}
                        aria-expanded={isOpen}
                        data-testid={`roadmap-toggle-${i + 1}`}
                      >
                        <span className={`font-mono text-xs tracking-[0.25em] uppercase shrink-0 ${i === 0 ? "text-gold" : "text-teal"}`}>
                          {phase.phase}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-display font-semibold text-mist text-base sm:text-xl uppercase tracking-wide truncate">
                            {phase.title}
                          </span>
                          <span className="block font-mono text-[10px] sm:text-xs text-slate mt-1 tracking-[0.2em] uppercase">{phase.years}</span>
                        </span>
                        <CaretDown
                          size={20}
                          className={`text-slate shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-teal" : ""}`}
                          aria-hidden="true"
                        />
                      </button>
                      <motion.div
                        initial={false}
                        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <ul className="px-6 sm:px-8 pb-6 pt-1 grid gap-2.5 sm:grid-cols-2 border-t border-line">
                          {phase.items.map((item) => (
                            <li key={item} className="flex items-start gap-3 text-sm text-slate pt-2.5">
                              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-teal shrink-0" aria-hidden="true" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Roadmap;
